import {
  collectRoleTokens,
  getDefaultRole,
  getMultiuserRole,
  getNavigationBlueprint,
  getRoleLabel as getConfiguredRoleLabel,
  getRoleLandingPath,
  getRoleOrder,
  inferRolesFromPath,
  normalizePath,
  normalizeRoleToken
} from '@/config/workspace'
import i18n from '@/i18n'

const ACTIVE_WORKSPACE_ROLE_KEY = 'active-workspace-role'
const WORKSPACE_ROLE_CHANGE_EVENT = 'workspace-role-changed'

function emitWorkspaceRoleChange (role) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return
  try {
    window.dispatchEvent(new CustomEvent(WORKSPACE_ROLE_CHANGE_EVENT, {
      detail: { role }
    }))
  } catch (err) {
    window.dispatchEvent(new Event(WORKSPACE_ROLE_CHANGE_EVENT))
  }
}

function getLocalRole () {
  if (typeof window === 'undefined' || !window.localStorage) return ''

  try {
    const raw = window.localStorage.getItem('objs')
    if (!raw) return ''
    const objs = JSON.parse(raw)
    const candidates = [
      objs && objs.role,
      objs && objs.user && objs.user.role,
      objs && objs.accounts && objs.accounts.role,
      objs && objs.accounts && objs.accounts[0] && objs.accounts[0].role
    ].filter(Boolean)
    return candidates.length ? String(candidates[0]).trim().toLowerCase() : ''
  } catch (err) {
    return ''
  }
}

function createBadge (value, color = 'danger') {
  const count = Number(value || 0)
  if (!count) return null
  return {
    color,
    text: String(count),
    shape: 'pill'
  }
}

function translate (key, fallback = '') {
  if (!key) return fallback
  const value = i18n && typeof i18n.t === 'function' ? i18n.t(key) : ''
  return value && value !== key ? value : fallback
}

function currentLocaleKey () {
  return i18n && i18n.locale === 'th' ? 'th' : 'en'
}

function translateRoleLabel (role) {
  const key = normalizeRoleToken(role)
  const catalog = {
    admin: 'shell.admin',
    committee: 'shell.committee',
    user: 'shell.user',
    multiuser: 'shell.multiUser'
  }
  return translate(catalog[key], getConfiguredRoleLabel(role))
}

function translateNavName (name) {
  const catalog = {
    Dashboard: 'shell.dashboard',
    'Sign Out': 'shell.signOut',
    Profile: 'shell.profile',
    Preferences: 'shell.preferences',
    General: 'shell.general',
    Permission: 'shell.permission',
    Admin: 'shell.admin',
    Committee: 'shell.committee',
    User: 'shell.user',
    'All Agreements': 'shell.navigation.allAgreements',
    'Audit Log': 'shell.navigation.auditLog',
    'Clause Library': 'shell.navigation.clauseLibrary',
    'Review Queue': 'shell.navigation.reviewQueue',
    'Translation Requests': 'shell.navigation.translationRequests',
    'Reports & Analytics': 'shell.navigation.reportsAnalytics',
    'Account Directory': 'shell.navigation.accountDirectory',
    'MCA Account Directory': 'shell.navigation.accountDirectory',
    'NewSystem Account Directory': 'shell.navigation.accountDirectory',
    'MOU Account Directory': 'shell.navigation.accountDirectory',
    'Role Permission Matrix': 'shell.navigation.rolePermissionMatrix',
    'MOU Permission Matrix': 'shell.navigation.rolePermissionMatrix',
    'MCA Permission Matrix': 'security.permissionMatrix.title',
    'NewSystem Permission Matrix': 'security.permissionMatrix.title',
    'Review Assignments': 'shell.navigation.reviewAssignments',
    'MOU Review Assignments': 'shell.navigation.reviewAssignments',
    'MCA Registry': 'nav.mcaRegistry',
    'New System Registry': 'nav.newSystemRegistry',
    'MCA Operations': 'nav.businessOperations',
    'New System Operations': 'nav.businessOperations',
    'Create Menu': 'shell.navigation.createMenu',
    'Create Group': 'shell.navigation.createGroup',
    Review: 'shell.navigation.review',
    'Review History': 'shell.navigation.reviewHistory',
    'User Manual': 'shell.navigation.userManual',
    'Help & Support': 'shell.navigation.helpSupport',
    'My Agreements': 'shell.navigation.myAgreements',
    'My Clause Library': 'shell.navigation.myClauseLibrary',
    'My Review Queue': 'shell.navigation.myReviewQueue',
    'My Translation Requests': 'shell.navigation.myTranslationRequests',
    'My Reports': 'shell.navigation.myReports'
  }
  return translate(catalog[name], name)
}

function toNavItem (item, counts) {
  if (item.kind === 'title') {
    return { _name: 'CSidebarNavTitle', _children: [translateNavName(item.value)] }
  }

  if (item.kind === 'dropdown') {
    return {
      _name: 'CSidebarNavDropdown',
      name: translateNavName(item.name),
      route: item.route,
      icon: item.icon,
      items: (item.items || []).map(child => ({ ...child, name: translateNavName(child.name) }))
    }
  }

  if (item.kind === 'item') {
    return {
      _name: 'CSidebarNavItem',
      name: translateNavName(item.name),
      to: item.to,
      icon: item.icon,
      badge: item.badgeKey ? createBadge(counts[item.badgeKey], item.badgeColor || 'danger') : null
    }
  }

  return null
}

function resolveMenuLabel (menu) {
  const locale = currentLocaleKey()
  if (Array.isArray(menu && menu.title)) {
    const direct = menu.title.find(item => item && item.key === locale && item.value)
    const english = menu.title.find(item => item && item.key === 'en' && item.value)
    const fallback = menu.title.find(item => item && item.value)
    if (direct && direct.value) return direct.value
    if (english && english.value) return english.value
    if (fallback && fallback.value) return fallback.value
  }
  return translateNavName(String(menu && menu.name ? menu.name : '').trim())
}

function getResponsibleRolesForMenu (menu) {
  if (Array.isArray(menu && menu.responsibleRoles) && menu.responsibleRoles.length) {
    return menu.responsibleRoles
  }
  return inferRolesFromPath(menu && menu.path ? menu.path : '')
}

function menuPathMatchesRoute (menuPath, routePath) {
  const normalizedMenuPath = normalizePath(menuPath)
  const normalizedRoutePath = normalizePath(routePath)
  if (!normalizedMenuPath || !normalizedRoutePath) return false
  if (normalizedMenuPath === normalizedRoutePath) return true

  const menuParts = normalizedMenuPath.split('/').filter(Boolean)
  const routeParts = normalizedRoutePath.split('/').filter(Boolean)
  if (menuParts.length !== routeParts.length) return false

  return menuParts.every((part, index) => part.startsWith(':') || part === routeParts[index])
}

function sortCatalogMenus (a, b) {
  const orderDelta = (Number(a && a.navOrder) || 0) - (Number(b && b.navOrder) || 0)
  if (orderDelta) return orderDelta
  return resolveMenuLabel(a).localeCompare(resolveMenuLabel(b))
}

function buildNavigationFromCatalog (role, counts = {}, menus = []) {
  const items = [
    {
      _name: 'CSidebarNavItem',
      name: translateNavName('Dashboard'),
      to: getRoleLandingPath(role),
      icon: 'cil-speedometer'
    }
  ]

  const visibleMenus = (menus || [])
    .filter(menu => menu && menu.state !== false && menu.showInNavigation !== false)
    .filter(menu => getResponsibleRolesForMenu(menu).includes(role))
    .sort(sortCatalogMenus)

  if (visibleMenus.length) {
    items.push({ _name: 'CSidebarNavTitle', _children: [translateRoleLabel(role)] })
  }

  const dropdowns = {}

  visibleMenus.forEach((menu) => {
    const navTarget = menu.navPath || menu.path
    if (!navTarget) return

    const entry = {
      _name: 'CSidebarNavItem',
      name: resolveMenuLabel(menu),
      to: navTarget,
      icon: menu.navIcon || 'cil-circle',
      badge: menu.navBadgeKey ? createBadge(counts[menu.navBadgeKey], 'danger') : null
    }

    if (menu.navGroup) {
      const dropdownKey = `${role}:${menu.navGroup}`
      if (!dropdowns[dropdownKey]) {
        dropdowns[dropdownKey] = {
          _name: 'CSidebarNavDropdown',
          name: translateNavName(menu.navGroup),
          route: normalizePath(navTarget),
          icon: menu.navGroupIcon || menu.navIcon || 'cil-folder-open',
          items: []
        }
        items.push(dropdowns[dropdownKey])
      }
      dropdowns[dropdownKey].items.push({
        name: resolveMenuLabel(menu),
        to: navTarget
      })
      return
    }

    items.push(entry)
  })

  if (items.length > 1) {
    items.push({ _name: 'CSidebarNavDivider' })
  }

  items.push({ _name: 'CSidebarNavItem', name: translateNavName('Sign Out'), to: '/logout', icon: 'cil-account-logout' })
  return [{ _name: 'CSidebarNav', _children: items }]
}

function buildRoleNavigation (role, counts = {}) {
  const items = [
    {
      _name: 'CSidebarNavItem',
      name: translateNavName('Dashboard'),
      to: getRoleLandingPath(role),
      icon: 'cil-speedometer'
    }
  ]

  getNavigationBlueprint(role).forEach((item) => {
    const mapped = toNavItem(item, counts)
    if (mapped) items.push(mapped)
  })

  if (items.length > 1) {
    items.push({ _name: 'CSidebarNavDivider' })
  }

  items.push({ _name: 'CSidebarNavItem', name: translateNavName('Sign Out'), to: '/logout', icon: 'cil-account-logout' })

  return [{ _name: 'CSidebarNav', _children: items }]
}

export function detectRole (store) {
  return getAssignedRoles(store)[0] || getDefaultRole()
}

export function getAssignedRoles (storeOrProfile) {
  const isStore = !!(storeOrProfile && storeOrProfile.getters)
  const profile = isStore
    ? storeOrProfile.getters['auth/profile']
    : storeOrProfile
  const tokens = collectRoleTokens(profile)

  if (isStore) {
    const assignments = storeOrProfile.state &&
      storeOrProfile.state.security &&
      Array.isArray(storeOrProfile.state.security.assignments)
      ? storeOrProfile.state.security.assignments
      : []

    assignments.forEach((assignment) => {
      const group = assignment && assignment.group ? assignment.group : null
      if (!group) return
      collectRoleTokens([
        group.name,
        group.title,
        group.value,
        group.code,
        group.key,
        group.role,
        group.roleName,
        group.label,
        group.groupLabel,
        assignment.label
      ], tokens)
    })
  }

  const roles = getRoleOrder().filter(role => tokens.has(role))
  const fallbackRole = normalizeRoleToken(getLocalRole())
  if (!roles.length && fallbackRole) {
    return [fallbackRole]
  }
  return roles
}

export function getWorkspaceRole (storeOrProfile) {
  const roles = getAssignedRoles(storeOrProfile)
  if (roles.length > 1) {
    const activeRole = getActiveWorkspaceRole(storeOrProfile)
    if (activeRole && roles.includes(activeRole)) return activeRole
    return getMultiuserRole()
  }
  return roles[0] || getDefaultRole()
}

export function resolveLandingPathForWorkspace(storeOrProfile, preferredLanding = '') {
  const roles = getAssignedRoles(storeOrProfile)
  const fallbackRole = roles.length > 1
    ? getMultiuserRole()
    : (roles[0] || getDefaultRole())
  const fallbackPath = getRoleLandingPath(fallbackRole)
  const nextLanding = String(preferredLanding || '').trim()

  if (!nextLanding) return fallbackPath

  const landingRoles = inferRolesFromPath(normalizePath(nextLanding))
  if (landingRoles.some(role => roles.includes(role))) {
    return nextLanding
  }

  return fallbackPath
}

export function getActiveWorkspaceRole (storeOrProfile) {
  const roles = getAssignedRoles(storeOrProfile)
  if (roles.length <= 1) return roles[0] || getDefaultRole()
  if (typeof window === 'undefined' || !window.localStorage) return ''
  const raw = String(window.localStorage.getItem(ACTIVE_WORKSPACE_ROLE_KEY) || '').trim().toLowerCase()
  return roles.includes(raw) ? raw : ''
}

export function setActiveWorkspaceRole (role, storeOrProfile) {
  if (typeof window === 'undefined' || !window.localStorage) return
  const normalized = normalizeRoleToken(role)
  const roles = getAssignedRoles(storeOrProfile)
  if (!normalized || !roles.includes(normalized)) {
    window.localStorage.removeItem(ACTIVE_WORKSPACE_ROLE_KEY)
    emitWorkspaceRoleChange(getMultiuserRole())
    return
  }
  window.localStorage.setItem(ACTIVE_WORKSPACE_ROLE_KEY, normalized)
  emitWorkspaceRoleChange(normalized)
}

export function clearActiveWorkspaceRole () {
  if (typeof window === 'undefined' || !window.localStorage) return
  window.localStorage.removeItem(ACTIVE_WORKSPACE_ROLE_KEY)
  emitWorkspaceRoleChange(getMultiuserRole())
}

export function workspaceRoleEventName () {
  return WORKSPACE_ROLE_CHANGE_EVENT
}

export function detectRouteRole (storeOrProfile, route) {
  const roles = getAssignedRoles(storeOrProfile)
  const requestedRole = normalizeRoleToken(route && route.query && route.query.workspace)
  if (requestedRole && roles.includes(requestedRole)) {
    return requestedRole
  }

  const menuCatalog = storeOrProfile && storeOrProfile.state && storeOrProfile.state.security && Array.isArray(storeOrProfile.state.security.catalogMenus)
    ? storeOrProfile.state.security.catalogMenus
    : []
  const matchedMenu = menuCatalog.find(menu => menuPathMatchesRoute(menu && menu.path, route && route.path))
  if (matchedMenu) {
    const matchedRoleFromMenu = getResponsibleRolesForMenu(matchedMenu).find(role => roles.includes(role))
    if (matchedRoleFromMenu) return matchedRoleFromMenu
  }

  const routeRoles = inferRolesFromPath(route && route.path ? route.path : '')
  const matchedRole = routeRoles.find(role => roles.includes(role))
  return matchedRole || roles[0] || getDefaultRole()
}

export function getRoleLabel (role) {
  return translateRoleLabel(role)
}

export function getRoleNavigation (role, counts = {}, menuCatalog = []) {
  if (Array.isArray(menuCatalog) && menuCatalog.length) {
    return buildNavigationFromCatalog(role, counts, menuCatalog)
  }
  return buildRoleNavigation(role, counts)
}

function appendBlueprintItems (items, blueprint, counts) {
  let added = 0
  ;(blueprint || []).forEach((item) => {
    const mapped = toNavItem(item, counts)
    if (mapped) {
      items.push(mapped)
      added += 1
    }
  })
  return added
}

function splitAdminBlueprintForCombinedNav () {
  const secondaryTitles = ['MCA Modules', 'System Administration']
  const primary = []
  const secondary = []
  let inSecondary = false

  getNavigationBlueprint('admin').forEach((item) => {
    if (item.kind === 'title' && secondaryTitles.includes(item.value)) {
      inSecondary = true
    }
    ;(inSecondary ? secondary : primary).push(item)
  })

  return { primary, secondary }
}

export function getWorkspaceNavigation (workspaceRole, assignedRoles = [], counts = {}, menuCatalog = []) {
  if (workspaceRole !== getMultiuserRole()) {
    return getRoleNavigation(workspaceRole, counts, menuCatalog)
  }

  if (Array.isArray(menuCatalog) && menuCatalog.length) {
    const items = [{
      _name: 'CSidebarNavItem',
      name: translateNavName('Dashboard'),
      to: getRoleLandingPath(getMultiuserRole()),
      icon: 'cil-speedometer'
    }]

    getRoleOrder().forEach((role) => {
      if (!assignedRoles.includes(role)) return
      const roleMenus = buildNavigationFromCatalog(role, counts, menuCatalog)
      const children = roleMenus[0] && Array.isArray(roleMenus[0]._children)
        ? roleMenus[0]._children.slice(1, -1).filter((child) => {
            const name = child && child._name
            return name !== 'CSidebarNavTitle' && name !== 'CSidebarNavDivider'
          })
        : []
      if (!children.length) return
      items.push({ _name: 'CSidebarNavTitle', _children: [translateRoleLabel(role)] })
      children.forEach(child => items.push(child))
      items.push({ _name: 'CSidebarNavDivider' })
    })

    items.push({ _name: 'CSidebarNavItem', name: translateNavName('Sign Out'), to: '/logout', icon: 'cil-account-logout' })
    return [{ _name: 'CSidebarNav', _children: items }]
  }

  const items = [{
    _name: 'CSidebarNavItem',
    name: translateNavName('Dashboard'),
    to: getRoleLandingPath(getMultiuserRole()),
    icon: 'cil-speedometer'
  }]

  const adminBlueprint = splitAdminBlueprintForCombinedNav()
  const orderedSections = [
    assignedRoles.includes('admin') ? adminBlueprint.primary : [],
    assignedRoles.includes('committee') ? getNavigationBlueprint('committee') : [],
    assignedRoles.includes('user') ? getNavigationBlueprint('user') : [],
    assignedRoles.includes('admin') ? adminBlueprint.secondary : []
  ]

  orderedSections.forEach((section) => {
    const added = appendBlueprintItems(items, section, counts)
    if (!added) return
    items.push({ _name: 'CSidebarNavDivider' })
  })

  items.push({ _name: 'CSidebarNavItem', name: translateNavName('Sign Out'), to: '/logout', icon: 'cil-account-logout' })

  return [{ _name: 'CSidebarNav', _children: items }]
}

function shouldKeepNavItem (item, canAccess) {
  if (!item || typeof item !== 'object') return false

  const itemName = String(item._name || '')
  if (itemName === 'CSidebarNavTitle' || itemName === 'CSidebarNavDivider') return true

  if (itemName === 'CSidebarNavItem') {
    const target = normalizePath(item.to || item.route)
    if (!target || inferRolesFromPath(target).length > 1) {
      return true
    }
    return canAccess(target, 'view', { keepWhenMissing: true })
  }

  if (itemName === 'CSidebarNavDropdown') {
    const children = Array.isArray(item.items) ? item.items.filter((child) => {
      const target = normalizePath(child && (child.to || child.route))
      if (!target || inferRolesFromPath(target).length > 1) return true
      return canAccess(target, 'view', { keepWhenMissing: true })
    }) : []

    if (!children.length) return false
    item.items = children
    return true
  }

  return true
}

export function filterNavigationByPermissions (navigation, canAccess) {
  if (typeof canAccess !== 'function') return navigation
  return (navigation || []).map((section) => {
    if (!section || !Array.isArray(section._children)) return section
    const nextChildren = []
    section._children.forEach((child) => {
      const cloned = child && typeof child === 'object'
        ? {
            ...child,
            items: Array.isArray(child.items) ? child.items.map(item => ({ ...item })) : child.items
          }
        : child
      if (shouldKeepNavItem(cloned, canAccess)) {
        nextChildren.push(cloned)
      }
    })
    return {
      ...section,
      _children: nextChildren
    }
  })
}
